import type { AssetWorkflowOptions } from '../../types';

export const addCarrierSettings: AssetWorkflowOptions = {
  workflowKey: 'createCarrierSetting',
  workflowIndex: 1,
  assetType: 'carrierSettings',
  workflowType: 'addAssetWorkflow',
  workflowName: 'Add Carrier Settings',
  workflowDescription: 'Add global per carrier settings',
  workflowStepOptions: [
    {
      workflowStepKey: 'carrierSelect',
      workflowStepNavigationName: 'Carrier Select',
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
        suffix: ' Settings',
      },
      workflowComponent: {
        workflowComponentType: 'carrierServiceMethodEntry',
        prompt: 'Which carrier does this apply to?',
        description: '',
        help: '',
        hideServiceMethods: true,
        singleSelect: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'carrierId',
      },
    },
    {
      workflowStepKey: 'partnerProvidedId',
      workflowStepNavigationName: 'Carrier Settings ID Alias',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: 'Enter a unique identifier for this carrier settings',
        description:
          'This is optional but will allow you to more easily reference these carrier settings properies in API calls.',
        help: '',
      },
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'partnerProvidedId',
      },
    },
    {
      workflowStepKey: 'name',
      workflowStepNavigationName: 'Carrier Settings Name',
      workflowComponent: {
        workflowComponentType: 'assetNameEntry',
        prompt: 'Add a name for the settings',
        description: '',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierSelect',
          schema: { type: 'string' },
        },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'name',
      },
    },
    {
      workflowStepKey: 'description',
      workflowStepNavigationName: 'Carrier Settings Description',
      workflowComponent: {
        workflowComponentType: 'textEntry',
        rows: 4,
        prompt: 'Enter a description (optional):',
        description: '',
        defaultValue: '',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'description',
      },
    },
    {
      workflowStepKey: 'allowLabelGenerationFailover',
      workflowStepNavigationName: 'Label Failover',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Do you want to enable label failover?',
        description:
          'Sometimes carrier label APIs will fail, in this case we can either not generate the label or use the next best carrier',
        help: `Sometimes carrier label APIs will fail, in this case we can either not generate the label or use the next best carrier`,
        options: [
          {
            optionText: 'Yes, allow failover',
            optionValue: true,
          },
          {
            optionText: `No, do not allow failover`,
            optionValue: false,
          },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'allowLabelGenerationFailover',
      },
    },
    {
      workflowStepKey: 'expectedShippingVolumeOveragePercentage',
      workflowStepNavigationName: 'Volume Overage',
      workflowComponent: {
        workflowComponentType: 'textEntry',
        rows: 1,
        validationRegex: new RegExp('^[0-9]*$'),
        validationMessage: 'Must enter a number',
        suffix: '%',
        prompt: 'What is your expected shipping volume overage percentage?',
        description:
          'If you have volume-tiered peak surcharges, this number represents the percentage difference between average daily volume versus holiday volume. So if you normally ship 50 packages per day and during peak you ship 100, you would enter 200 for this percentage value',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'expectedShippingVolumeOveragePercentage',
      },
    },
    {
      workflowStepKey: 'hazmatLimitedQuantityExempt',
      workflowStepNavigationName: 'LQ Exempt',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Are you exempt from limited quantity/hazmat restrictions',
        description: '',
        help: ``,
        options: [
          {
            optionText: 'Yes we are exempt from limited quantity restrictions',
            optionValue: true,
          },
          {
            optionText: `No we are not exempt from limited quantity restrictions`,
            optionValue: false,
          },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'hazmatLimitedQuantityExempt',
      },
    },
    {
      workflowStepKey: 'peakSurchargeEligible',
      workflowStepNavigationName: 'Peak Surcharge',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Are you eligible for peak surcharges?',
        description: '',
        help: ``,
        options: [
          {
            optionText: `Yes, we are eligible for this carrier's peak surcharges.`,
            optionValue: true,
          },
          {
            optionText: `No, we are not eligible for this carrier's peak surcharges.`,
            optionValue: false,
          },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'peakSurchargeEligible',
      },
    },
    {
      workflowStepKey: 'packageDimOverride',
      workflowStepNavigationName: 'Dimension Override',
      workflowComponent: {
        workflowComponentType: 'carrierDimensionSelect',
        prompt: 'Select carrier dimension override',
        description: '',
        help: '',
      },
      calculatedComponentProps: {
        selectedCarrierId: {
          property: 'carrierSelect',
          type: 'workflowStep',
        },
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'dimensionGroupId',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierSelect',
          schema: { type: 'string' },
        },
      ],
      optional: true,
      shipiumOnly: true,
    },
    {
      workflowStepKey: 'tagEntry',
      workflowStepNavigationName: 'Add Tags',
      workflowComponent: {
        workflowComponentType: 'tagEntry',
        prompt: 'Tag this entry?',
        description: 'Add tags to these carrier settings to help identify it later',
        help: '',
        defaultValue: null,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'tagIds',
      },
    },
    {
      workflowStepKey: 'tenantSelect',
      workflowStepNavigationName: 'Tenant Select',
      workflowComponent: {
        assetType: 'tenant',
        optional: true,
        workflowComponentType: 'tenantSelect',
        prompt: 'Do these carrier settings belong to a specific tenant?',
        description: '',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          assetType: 'tenant',
          schema: { type: 'array', minItems: 1 },
        },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'tenant',
        linkType: 'Tenant',
      },
      optional: true,
    },
    {
      workflowStepKey: 'carrierSettingsSummary',
      workflowStepNavigationName: 'Carrier Settings Summary',
      workflowComponent: {
        workflowComponentType: 'workflowSummary',
        title: 'Carrier Settings Summary',
        description: 'The following values will be saved.  Please review your selections.',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
  ],
};
